import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';

import { getProduits, searchProduits } from '@/routes';
import type { ApiResponseType, ProduitType } from '@/types';
import type { ProduitsListeItemType } from '@/types/produit';
import { formatProduitsToListeItem } from '@/utils';

const MIN_SEARCH_LENGTH = 2;
const DEBOUNCE_DELAY = 300;

type ProduitOption = {
  key: number;
  value: number;
  text: string;
};

const useProduitSearch = () => {
  const [loading, setLoading] = React.useState(false);
  const [produits, setProduits] = React.useState<ProduitsListeItemType[]>([]);

  // Premiers produits chargés d'emblée, réaffichés quand la recherche est trop courte.
  const initialProduitsRef = React.useRef<ProduitsListeItemType[]>([]);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  React.useEffect(() => {
    let cancelled = false;

    request
      .get(getProduits({ comptant: true }))
      .then(({ data }: ApiResponseType<ProduitType>) => {
        if (cancelled) return;
        const formatted = formatProduitsToListeItem(data);
        initialProduitsRef.current = formatted;
        setProduits((prev) => (_.isEmpty(prev) ? formatted : prev));
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchProduits = React.useCallback((query: string) => {
    if (query.length < MIN_SEARCH_LENGTH) {
      setProduits(initialProduitsRef.current);
      setLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);

    request
      .get(searchProduits({ query, perPage: 10 }), { signal: controller.signal })
      .then(({ data }: ApiResponseType<ProduitType>) => {
        setProduits(formatProduitsToListeItem(data));
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          setProduits([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      });
  }, []);

  const debouncedFetch = React.useRef(
    _.debounce((query: string) => fetchProduits(query), DEBOUNCE_DELAY),
  );

  React.useEffect(
    () => () => {
      debouncedFetch.current.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    },
    [],
  );

  const onSearchChange = React.useCallback(
    (e: React.SyntheticEvent, { searchQuery }: { searchQuery: string }) => {
      debouncedFetch.current(searchQuery);
    },
    [],
  );

  const options = React.useMemo<ProduitOption[]>(
    () =>
      _.map(produits, (p) => ({
        key: p.identifiant,
        value: p.identifiant,
        text: `${p.code ? `${p.code} - ` : ''}${p.libelle}`,
      })),
    [produits],
  );

  return { loading, options, produits, onSearchChange };
};

export default useProduitSearch;
